import React, { useState, useEffect, useRef, useMemo } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/apiClient";
import {
  Gauge, MapPin, Truck, Circle, ClipboardText,
  MagnifyingGlass, SignOut, List, X, UsersThree, DeviceMobile, Wrench, Buildings, Gear, Flag, BookOpen,
} from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const NAV = [
  { to: "/", label: "Dashboard", icon: Gauge, end: true },
  { to: "/technician", label: "Technician Hub", icon: DeviceMobile },
  { to: "/vehicles", label: "Dumpers", icon: Truck },
  { to: "/tyres", label: "Tyre Register", icon: Circle },
  { to: "/activity", label: "Activity", icon: ClipboardText },
  { to: "/competition", label: "Competition", icon: Flag },
  { to: "/guide", label: "Guide", icon: BookOpen },
  { to: "/customers", label: "Customers", icon: Buildings, min: "admin" },
  { to: "/users", label: "Users", icon: UsersThree, min: "admin" },
  { to: "/settings", label: "Settings", icon: Gear, min: "admin" },
];

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const navigate = useNavigate();
  const boxRef = useRef();

  useEffect(() => {
    if (q.length < 2) { setResults(null); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/search?q=${encodeURIComponent(q)}`); setResults(data); } catch (e) { console.error("Search failed", e); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setResults(null); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const go = (r) => {
    setResults(null); setQ("");
    if (r.type === "tyre" || r.type === "puncture") navigate(`/tyres/${r.id}`);
    else if (r.type === "vehicle") navigate(`/vehicles/${r.id}`);
    else if (r.type === "site") navigate(`/sites`);
  };

  const groups = results ? Object.entries(results).filter(([, v]) => v.length) : [];

  return (
    <div className="relative min-w-0 flex-1 max-w-md" ref={boxRef}>
      <div className="flex items-center gap-2 rounded-sm border border-zinc-800 bg-zinc-950 px-3 py-2">
        <MagnifyingGlass size={16} className="text-zinc-500" />
        <input data-testid="global-search-input" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search serial, dumper #, report #..." className="w-full bg-transparent text-sm outline-none placeholder:text-zinc-600" />
      </div>
      {results && (
        <div data-testid="search-results" className="absolute z-50 mt-1 w-full rounded-sm border border-zinc-800 bg-zinc-900 shadow-xl max-h-96 overflow-auto">
          {groups.length === 0 && <div className="px-3 py-3 text-sm text-zinc-500">No results</div>}
          {groups.map(([grp, items]) => (
            <div key={grp}>
              <div className="overline px-3 pt-2 text-zinc-500">{grp}</div>
              {items.map((r) => (
                <button key={r.id + r.label} onClick={() => go(r)} data-testid={`search-result-${r.id}`}
                  className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-zinc-800">
                  <span className="font-mono">{r.label}</span>
                  <span className="text-xs text-zinc-500">{r.sub || r.type}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AppLayout({ children }) {
  const { user, logout, hasRole, currentSite, customers, currentCustomer, setCurrentCustomer } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useMemo(() => NAV.filter((n) => !n.min || hasRole(n.min)), [hasRole]);

  return (
    <div className="flex min-h-screen bg-zinc-950">
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 transform border-r border-zinc-800 bg-zinc-950 pt-[env(safe-area-inset-top)] transition-transform duration-150 md:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex h-16 items-center gap-2 border-b border-zinc-800 bg-zinc-900/40 px-5">
          <div className="flex h-8 w-8 items-center justify-center rounded-sm bg-amber-500 ring-1 ring-amber-400/40">
            <Wrench size={18} weight="bold" className="text-zinc-950" />
          </div>
          <div>
            <div className="font-display text-sm font-extrabold tracking-tight leading-none">TYREOPS</div>
            <div className="overline text-zinc-500">Fleet Tyre Intel</div>
          </div>
        </div>
        <nav className="flex flex-col gap-0.5 p-3">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
              data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
              className={({ isActive }) => `flex items-center gap-3 rounded-sm border-l-2 px-3 py-2.5 text-sm transition-colors duration-150 ${isActive ? "border-amber-500 bg-amber-500/10 font-semibold text-amber-400" : "border-transparent text-zinc-400 hover:bg-zinc-900 hover:text-zinc-50"}`}>
              {({ isActive }) => (<><n.icon size={18} weight={isActive ? "fill" : "regular"} />{n.label}</>)}
            </NavLink>
          ))}
        </nav>
      </aside>

      {open && <div className="fixed inset-0 z-30 bg-black/60 md:hidden" onClick={() => setOpen(false)} />}

      <div className="flex min-w-0 flex-1 flex-col md:ml-64">
        <header className="sticky top-0 z-20 flex min-h-[4rem] items-center gap-3 border-b border-zinc-800 bg-zinc-950 px-4 pt-[env(safe-area-inset-top)]">
          <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="menu-toggle">
            {open ? <X size={22} /> : <List size={22} />}
          </button>
          <GlobalSearch />
          <div className="ml-auto flex items-center gap-3">
            {customers?.length > 0 && (
              <Select value={currentCustomer?.id || ""} onValueChange={(id) => { const c = customers.find((x) => x.id === id); if (c) { setCurrentCustomer(c); window.location.reload(); } }}>
                <SelectTrigger data-testid="customer-switcher" className="hidden h-9 w-48 border-amber-500/30 bg-zinc-900 text-amber-400 sm:flex">
                  <div className="flex items-center gap-1.5 truncate"><Buildings size={14} /><SelectValue placeholder="Select customer" /></div>
                </SelectTrigger>
                <SelectContent>
                  {customers.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`switch-customer-${c.id}`}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {currentSite && (
              <div className="hidden items-center gap-1 rounded-sm border border-amber-500/30 bg-amber-500/10 px-2 py-1 text-xs font-bold text-amber-400 sm:flex" data-testid="header-site">
                <MapPin size={13} /> {currentSite.name}
              </div>
            )}
            <div className="hidden text-right sm:block">
              <div className="text-sm font-semibold leading-none">{user?.name}</div>
            </div>
            <div className="flex h-9 w-9 items-center justify-center rounded-sm bg-zinc-800 text-sm font-bold">{user?.name?.[0]}</div>
            <button data-testid="logout-btn" onClick={logout} className="rounded-sm border border-zinc-800 p-2 text-zinc-400 hover:text-rose-400 transition-colors">
              <SignOut size={18} />
            </button>
          </div>
        </header>
        <main className="min-w-0 flex-1 overflow-x-hidden p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
